import './MapOverview.css';
import React, {useEffect, useRef, useState} from 'react';
import {get, getDatabase, ref} from 'firebase/database';
import MapComponent from './MapComponent';
import BuildingListItem from './BuildingListItem';
import {Helmet} from "react-helmet-async";
import CustomDropdown, {CustomDropdownOption} from "../Components/Dropdown";
import {Button} from "react-bootstrap";
import {BuildingSummary, CoordinatesModel} from "../types/protocols";
import {useBuildingsData} from "../Helpers/BuildingSummariesContext";
import {BuildingType} from "../types/BuildingTypeEnums";
import CustomMultiSelect from "../Components/CustomMultiSelect";
import {translate} from "../types/EnumTranslations";

interface LocationCategories {
    category1?: Category;
    category2?: Category;
    category3?: Category;
    category4?: Category;
    category5?: Category;
    labels: {
        category1: string;
        category2: string;
        category3: string;
        category4: string;
        category5: string;
    };
}

interface Category {
    [key: string]: {
        name: string;
        parent?: string;
    };
}

interface BuildingTypeOption {
    label: string;
    value: BuildingType;
}

const STORAGE_KEYS = {
    CATEGORY_1: 'mapOverview_selectedCategory1',
    CATEGORY_2: 'mapOverview_selectedCategory2',
    CATEGORY_3: 'mapOverview_selectedCategory3',
    CATEGORY_4: 'mapOverview_selectedCategory4',
    CATEGORY_5: 'mapOverview_selectedCategory5',
    BUILDING_TYPES: 'mapOverview_selectedBuildingTypes'
};

const saveToStorage = (key: string, value: any) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.warn('Failed to save to localStorage:', error);
    }
};

const loadFromStorage = (key: string, defaultValue: any) => {
    try {
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
        console.warn('Failed to load from localStorage:', error);
        return defaultValue;
    }
};

export interface MapOverviewProps {}

const MapOverview: React.FC<MapOverviewProps> = () => {
    // ------------------------ State ------------------------
    const [locationCategories, setLocationCategories] = useState<LocationCategories | null>(null);

    // Buildings data
    const { buildings, loadingSummaries: buildingsLoading, summariesError: buildingsError } = useBuildingsData();
    const [filteredBuildings, setFilteredBuildings] = useState<BuildingSummary[]>([]);

    // Category filters - Load from localStorage on initialization
    const [selectedCategory1, setSelectedCategory1] = useState<string>(() =>
        loadFromStorage(STORAGE_KEYS.CATEGORY_1, '')
    );
    const [selectedCategory2, setSelectedCategory2] = useState<string>(() =>
        loadFromStorage(STORAGE_KEYS.CATEGORY_2, '')
    );
    const [selectedCategory3, setSelectedCategory3] = useState<string>(() =>
        loadFromStorage(STORAGE_KEYS.CATEGORY_3, '')
    );
    const [selectedCategory4, setSelectedCategory4] = useState<string>(() =>
        loadFromStorage(STORAGE_KEYS.CATEGORY_4, '')
    );
    const [selectedCategory5, setSelectedCategory5] = useState<string>(() =>
        loadFromStorage(STORAGE_KEYS.CATEGORY_5, '')
    );

    // Building type filter - Load from localStorage on initialization
    const [selectedBuildingTypes, setSelectedBuildingTypes] = useState<BuildingType[]>(() =>
        loadFromStorage(STORAGE_KEYS.BUILDING_TYPES, [
            BuildingType.standard,
            BuildingType.complement,
            BuildingType.toiletArchipelago,
            BuildingType.toiletReserve,
            BuildingType.other
        ])
    );

    const [searchTerm, setSearchTerm] = useState<string>(loadFromStorage('mapOverview_searchTerm', ''));

    const [highlightedKey, setHighlightedKey] = useState<string>('');
    const listItemRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});
    const [popupInfo, setPopupInfo] = useState<{
        id: string;
        coordinates: [number, number];
    } | null>(null);
    const [shouldRefocusMap, setShouldRefocusMap] = useState(true);

    // ------------------------ Building Type Options ------------------------
    const buildingTypeOptions: BuildingTypeOption[] = [
        { label: translate(BuildingType.standard), value: BuildingType.standard },
        { label: translate(BuildingType.complement), value: BuildingType.complement },
        { label: translate(BuildingType.toiletArchipelago), value: BuildingType.toiletArchipelago },
        { label: translate(BuildingType.toiletReserve), value: BuildingType.toiletReserve },
        { label: translate(BuildingType.other), value: BuildingType.other },
    ];

    // ------------------------ Effects ------------------------
    useEffect(() => {
        fetchLocationCategories().then(r => {});
    }, []);

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.CATEGORY_1, selectedCategory1);
    }, [selectedCategory1]);

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.CATEGORY_2, selectedCategory2);
    }, [selectedCategory2]);

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.CATEGORY_3, selectedCategory3);
    }, [selectedCategory3]);

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.CATEGORY_4, selectedCategory4);
    }, [selectedCategory4]);

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.CATEGORY_5, selectedCategory5);
    }, [selectedCategory5]);

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.BUILDING_TYPES, selectedBuildingTypes);
    }, [selectedBuildingTypes]);

    useEffect(() => {
        if (!buildings) return;
        const nextFiltered = filterBuildings(buildings, {
            selectedCategory1,
            selectedCategory2,
            selectedCategory3,
            selectedCategory4,
            selectedCategory5,
            selectedBuildingTypes,
        });
        setFilteredBuildings(nextFiltered);
    }, [
        buildings,
        selectedCategory1,
        selectedCategory2,
        selectedCategory3,
        selectedCategory4,
        selectedCategory5,
        selectedBuildingTypes,
    ]);

    useEffect(() => {
        if (!highlightedKey) return;
        const element = listItemRefs.current[highlightedKey];
        if (!element) return;
        const rect = element.getBoundingClientRect();
        const isInView =
            rect.top >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

        if (!isInView) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [highlightedKey]);

    // ------------------------ Data Fetching ------------------------
    const fetchLocationCategories = async () => {
        try {
            const db = getDatabase();
            const categoryRef = ref(db, 'locationCategories');
            const snapshot = await get(categoryRef);

            if (snapshot.exists()) {
                const data = snapshot.val() as LocationCategories;
                setLocationCategories(data);
            } else {
                console.log('No location categories data available in the snapshot');
            }
        } catch (err) {
            console.error('Failed to load categories data', err);
        }
    };

    // ------------------------ Helpers & Handlers ------------------------
    const filterBuildings = (
        allBuildings: BuildingSummary[],
        {
            selectedCategory1,
            selectedCategory2,
            selectedCategory3,
            selectedCategory4,
            selectedCategory5,
            selectedBuildingTypes,
        }: {
            selectedCategory1: string;
            selectedCategory2: string;
            selectedCategory3: string;
            selectedCategory4: string;
            selectedCategory5: string;
            selectedBuildingTypes: BuildingType[];
        }
    ): BuildingSummary[] => {
        return allBuildings.filter((building) => {
            const categories = building.locationCategories;

            // Filter by location categories
            const categoryMatch = (
                (!selectedCategory1 || categories?.category1?.key === selectedCategory1) &&
                (!selectedCategory2 || categories?.category2?.key === selectedCategory2) &&
                (!selectedCategory3 || categories?.category3?.key === selectedCategory3) &&
                (!selectedCategory4 || categories?.category4?.key === selectedCategory4) &&
                (!selectedCategory5 || categories?.category5?.key === selectedCategory5)
            );

            // Filter by building types - if types are selected, only show those types
            const buildingTypeMatch = selectedBuildingTypes.length === 0 ||
                (building.buildingType !== undefined && selectedBuildingTypes.includes(building.buildingType));

            return categoryMatch && buildingTypeMatch;
        })
            .sort((a, b) => {
                // Sort alphabetically by name (case-insensitive)
                const nameA = a.name?.toLowerCase() || '';
                const nameB = b.name?.toLowerCase() || '';
                return nameA.localeCompare(nameB);
            });;
    };

    const getOptions = (category: Category | undefined, parentKey?: string): CustomDropdownOption[] => {
        if (!category) return [];
        return Object.entries(category)
            .filter(([_, value]) => value.parent === parentKey)
            .sort(([, a], [, b]) => a.name.localeCompare(b.name))
            .map(([key, value]) => ({ key, name: value.name }));
    };

    /**
     * Reset categories from `level` onward.
     * e.g. if level=2, reset category2..5.
     */
    const resetSubCategories = (level: number) => {
        if (level <= 1) {
            setSelectedCategory1('');
        }
        if (level <= 2) {
            setSelectedCategory2('');
        }
        if (level <= 3) {
            setSelectedCategory3('');
        }
        if (level <= 4) {
            setSelectedCategory4('');
        }
        if (level <= 5) {
            setSelectedCategory5('');
        }
    };

    const resetAllFilters = () => {
        resetSubCategories(1);
        setSelectedBuildingTypes([
            BuildingType.standard,
            BuildingType.complement,
            BuildingType.toiletArchipelago,
            BuildingType.toiletReserve,
            BuildingType.other
        ]);
        setShouldRefocusMap(true);
    };

    // ------------------------ Derived Data ------------------------
    const category1Options = getOptions(locationCategories?.category1);
    const category2Options = getOptions(locationCategories?.category2, selectedCategory1);
    const category3Options = getOptions(locationCategories?.category3, selectedCategory2);
    const category4Options = getOptions(locationCategories?.category4, selectedCategory3);
    const category5Options = getOptions(locationCategories?.category5, selectedCategory4);

    // Coordinates for map zoom/focus (only filtered buildings)
    const filteredCoordinates = filteredBuildings
        .filter(
            (building): building is BuildingSummary & { coordinates: CoordinatesModel } =>
                building.coordinates !== undefined && building.coordinates !== null
        )
        .map((building) => ({
            coords: [building.coordinates.latitude, building.coordinates.longitude] as [number, number],
        }));

    // All coordinates for map display (all buildings with coordinates)
    const allCoordinates = buildings
        ?.filter((b) => b.coordinates)
        .map((b) => {
            const isFiltered = filteredBuildings.some(fb => fb.key === b.key);
            return {
                coords: [b.coordinates!.latitude, b.coordinates!.longitude] as [number, number],
                building: b,
                isFiltered: isFiltered,
            };
        }) || [];

    const hasAnyFilters = selectedCategory1 !== '' || selectedBuildingTypes.length < buildingTypeOptions.length;

    // ------------------------ Render ------------------------
    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Helmet>
                <title>Molnfastigheter</title>
            </Helmet>

            <div
                style={{
                    paddingLeft: '20px',
                    paddingTop: '10px',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-end',
                    gap: '20px',
                    flexWrap: 'wrap'
                }}
            >
                {
                    locationCategories &&
                    <>
                        {/* Building Type MultiSelect */}
                        <CustomMultiSelect
                            label="Byggnadstyp"
                            options={buildingTypeOptions}
                            value={selectedBuildingTypes}
                            onChange={(value) => {
                                setSelectedBuildingTypes(value);
                                setShouldRefocusMap(true);
                            }}
                            maxSelectedLabels={1}
                            selectedItemsLabel="{0} byggnadstyper"
                            placeholder="Välj byggnadstyper"
                        />

                        {/* Category 1 */}
                        <CustomDropdown
                            label={locationCategories?.labels.category1 || 'Kategori 1'}
                            options={category1Options}
                            selectedKey={selectedCategory1}
                            onSelect={(key) => {
                                setSelectedCategory1(key);
                                resetSubCategories(2);
                                setShouldRefocusMap(true);
                            }}
                            disabled={false}
                        />

                        {locationCategories?.labels.category2 && (
                            <CustomDropdown
                                label={locationCategories.labels.category2}
                                options={category2Options}
                                selectedKey={selectedCategory2}
                                onSelect={(key) => {
                                    setSelectedCategory2(key);
                                    resetSubCategories(3);
                                    setShouldRefocusMap(true);
                                }}
                                disabled={!selectedCategory1}
                            />
                        )}

                        {locationCategories?.labels.category3 && (
                            <CustomDropdown
                                label={locationCategories.labels.category3}
                                options={category3Options}
                                selectedKey={selectedCategory3}
                                onSelect={(key) => {
                                    setSelectedCategory3(key);
                                    resetSubCategories(4);
                                    setShouldRefocusMap(true);
                                }}
                                disabled={!selectedCategory2}
                            />
                        )}

                        {locationCategories?.labels.category4 && (
                            <CustomDropdown
                                label={locationCategories.labels.category4}
                                options={category4Options}
                                selectedKey={selectedCategory4}
                                onSelect={(key) => {
                                    setSelectedCategory4(key);
                                    resetSubCategories(5);
                                    setShouldRefocusMap(true);
                                }}
                                disabled={!selectedCategory3}
                            />
                        )}

                        {locationCategories?.labels.category5 && (
                            <CustomDropdown
                                label={locationCategories.labels.category5}
                                options={category5Options}
                                selectedKey={selectedCategory5}
                                onSelect={(key) => {
                                    setSelectedCategory5(key);
                                    setShouldRefocusMap(true);
                                }}
                                disabled={!selectedCategory4}
                            />
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Button
                                style={{
                                    minWidth: '100px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    marginBottom: '1rem',
                                }}
                                variant="secondary"
                                disabled={!hasAnyFilters}
                                onClick={resetAllFilters}
                            >
                                <i className="bi bi-x-circle" style={{ marginRight: '8px' }}></i>
                                Rensa filter
                            </Button>
                        </div>
                    </>
                }

            </div>

            <div className="search-bar">
                <input
                    type="text"
                    placeholder="Sök byggnad..."
                    value={searchTerm}
                    onChange={e => {
                        setSearchTerm(e.target.value);
                        setShouldRefocusMap(true);
                    }}
                />
                <i className="pi pi-search search-icon" />
            </div>

            <div style={{
                display: 'flex',
                flex: 1,
                overflow: 'hidden',
            }}>
                {/* Building List - Only show filtered buildings */}
                <div
                    style={{
                        width: '340px',
                        overflowY: 'auto',
                        maxHeight: '100%',
                    }}
                >
                    <ul
                        style={{
                            listStyleType: 'none',
                            padding: 0,
                            paddingBottom: '1rem',
                            margin: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem'
                        }}
                    >
                        {filteredBuildings.map((building) => (
                            <BuildingListItem
                                key={building.key}
                                building={building}
                                highlighted={highlightedKey === building.key}
                                onClick={() => {
                                    setShouldRefocusMap(false);
                                    setHighlightedKey(building.key!);
                                    if (building.coordinates?.latitude) {
                                        setPopupInfo({
                                            id: building.key!,
                                            coordinates: [
                                                building.coordinates!.latitude,
                                                building.coordinates!.longitude,
                                            ],
                                        });
                                    } else {
                                        setPopupInfo({
                                            id: building.key!,
                                            coordinates: [0, 0],
                                        });
                                    }
                                }}
                                visible={building.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) || false}
                                ref={(el) => (listItemRefs.current[building.key!] = el)}
                                category1Label={locationCategories?.labels.category1 || 'Kategori 1'}
                                category2Label={locationCategories?.labels.category2 || 'Kategori 2'}/>
                        ))}
                    </ul>
                </div>

                {/* Map - Show all buildings, but only focus on filtered ones */}
                <div className={"map_container"} style={{visibility: 'visible'}} >
                    <MapComponent
                        allCoordinates={allCoordinates}
                        focusCoordinates={filteredCoordinates}
                        highlightedId={highlightedKey}
                        popupInfo={popupInfo}
                        shouldResizeBounds={shouldRefocusMap}
                        buildingSummaries={buildings}
                        onMarkerClick={(key) => {
                            setShouldRefocusMap(false);
                            setHighlightedKey(key);
                            const building = buildings?.find((b) => b.key === key);
                            if (building?.coordinates) {
                                setPopupInfo({
                                    id: key,
                                    coordinates: [
                                        building.coordinates.latitude,
                                        building.coordinates.longitude,
                                    ],
                                });
                            }
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default MapOverview;